import { canAccess } from '~/utils/rbac';
import { BaseSearchProps, Widget } from '../types';

export const searchWidgets = async ({
  searchTerm,
  onSuccess,
  onError,
  onEmpty,
}: BaseSearchProps<Widget>): Promise<void> => {
  const widgets: Widget[] = [];

  if (canAccess('widget:allocation')) {
    widgets.push({
      id: 'allocation',
      name: 'Reference Universe',
      description: 'View and analyze reference universe allocations',
      type: 'widget',
      imageUrl: '/images/widgets/allocation.png',
    });
  }

  if (canAccess('widget:allocation_table')) {
    widgets.push({
      id: 'allocation-table',
      name: 'Current Holdings',
      description:
        'Interactive table of current and proposed allocations with filtering, sorting, and CSV export',
      type: 'widget',
      imageUrl: '/images/widgets/current-holdings.png',
    });
    widgets.push({
      id: 'allocation-table-universe',
      name: 'Reference Universe',
      description:
        'Interactive table of reference universe and proposed allocations with filtering, sorting, and CSV export',
      type: 'widget',
      imageUrl: '/images/widgets/current-holdings.png',
    });
  }

  if (canAccess('widget:sector_allocation')) {
    widgets.push({
      id: 'sector-allocation',
      name: 'Allocation Analysis',
      description:
        'Tree view of portfolio allocations grouped by sector with expandable holdings and aggregated totals',
      type: 'widget',
      imageUrl: '/images/widgets/current-holdings.png',
    });
  }

  if (canAccess('widget:proposed_portfolio')) {
    widgets.push({
      id: 'proposed-portfolio',
      name: 'Current VS AI Portfolio',
      description: 'View proposed portfolio metrics and performance indicators',
      type: 'widget',
      imageUrl: '/images/widgets/proposed-portfolio.png',
    });
  }

  if (canAccess('widget:ai_recommendations')) {
    widgets.push({
      id: 'ai-recommendations',
      name: 'AI Analysis Results',
      description:
        'AI-powered investment recommendations and portfolio optimization suggestions',
      type: 'widget',
      imageUrl: '/images/widgets/ai-recommendations.png',
    });
  }

  if (canAccess('widget:recommendation_report')) {
    widgets.push({
      id: 'recommendation-report',
      name: 'AI Recommendation Report',
      description:
        'Comprehensive AI-generated recommendation reports with detailed analysis and market outlook',
      type: 'widget',
      imageUrl: '/images/widgets/ai-recommendations.png',
    });
  }

  if (canAccess('widget:projected_risk_metrics')) {
    widgets.push({
      id: 'projected-risk-metrics',
      name: 'Projected Risk: Current vs Proposed',
      description:
        'View projected risk metrics and performance indicators with current vs projected values',
      type: 'widget',
      imageUrl: '/images/widgets/projected-risk-metrics.png',
    });
  }

  if (canAccess('widget:expected_impact')) {
    widgets.push({
      id: 'expected-impact',
      name: 'Expected Impact',
      description:
        'View expected impact metrics and projected improvements with visual indicators',
      type: 'widget',
      imageUrl: '/images/widgets/expected-impacts.png',
    });
  }

  if (canAccess('widget:risk_alerts')) {
    widgets.push({
      id: 'risk-alerts',
      name: 'Risk Alerts',
      description: 'AI-powered risk monitoring and alert system',
      type: 'widget',
      imageUrl: '/images/widgets/risk-alerts.png',
    });
  }

  if (canAccess('widget:market_analysis')) {
    widgets.push({
      id: 'market-analysis',
      name: 'Market Analysis',
      description: 'Real-time market data and analysis',
      type: 'widget',
      imageUrl: '/images/widgets/market-analysis.png',
    });
  }

  if (canAccess('widget:news_impact_analysis')) {
    widgets.push({
      id: 'news-impact-analysis',
      name: 'News Impact Analysis',
      description: 'News sentiment and impact analysis',
      type: 'widget',
      imageUrl: '/images/widgets/news-impact-analysis.png',
    });
  }

  if (canAccess('widget:historical_returns')) {
    widgets.push({
      id: 'historical-returns',
      name: 'Historical Returns',
      description:
        'Compare proposed portfolio vs AI recommended portfolio performance over time',
      type: 'widget',
      imageUrl: '/images/widgets/historical-returns.png',
    });
  }

  if (canAccess('widget:forward_returns')) {
    widgets.push({
      id: 'forward-returns',
      name: 'Forward & Real Returns',
      description:
        'Project future portfolio performance using EWMA-based simulation with historical context',
      type: 'widget',
      imageUrl: '/images/widgets/historical-returns.png',
    });
  }

  if (canAccess('widget:asset_breakdown')) {
    widgets.push({
      id: 'asset-breakdown-proposed',
      name: 'Forward Returns Breakdown (AI Portfolio)',
      description:
        'Stacked area chart showing cumulative returns by asset for the AI-optimized portfolio',
      type: 'widget',
      imageUrl: '/images/widgets/asset-breakdown.png',
    });
  }

  if (canAccess('widget:asset_breakdown')) {
    widgets.push({
      id: 'asset-breakdown-current',
      name: 'Forward Returns Breakdown (Current Portfolio)',
      description:
        'Stacked area chart showing cumulative returns by asset for the current portfolio',
      type: 'widget',
      imageUrl: '/images/widgets/asset-breakdown.png',
    });
  }

  if (canAccess('widget:correlation_cluster')) {
    widgets.push({
      id: 'correlation-cluster',
      name: 'Correlation Cluster Analysis',
      description:
        'Interactive visualization showing asset correlations, allocations, and drawdowns using MDS positioning',
      type: 'widget',
      imageUrl: '/images/widgets/correlation-cluster.png',
    });
  }

  if (canAccess('widget:ems_blotter')) {
    widgets.push({
      id: 'ems-blotter',
      name: 'EMS Blotter',
      description: 'Execution Management System trading blotter',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  if (canAccess('widget:fund_list')) {
    widgets.push({
      id: 'fund-list',
      name: 'Fund List',
      description: 'Interactive list of funds',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  if (canAccess('widget:companies_per_sector')) {
    widgets.push({
      id: 'companies-per-sector',
      name: 'Allocation Breakdown',
      description: 'Visual breakdown of portfolio allocations',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  if (canAccess('widget:invested_per_sector')) {
    widgets.push({
      id: 'invested-per-sector',
      name: 'Allocation Breakdown',
      description: 'Visual breakdown of portfolio allocations',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  if (canAccess('widget:aggr_gross_irr')) {
    widgets.push({
      id: 'aggr-gross-irr',
      name: 'Aggregate Gross Irr Line Chart',
      description: 'Interactive chart showing fund performance over time',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  if (canAccess('widget:aggr_gross_moic')) {
    widgets.push({
      id: 'aggr-gross-moic',
      name: 'Aggregate Gross Moic Line Chart',
      description: 'Interactive chart showing fund performance over time',
      type: 'widget',
      imageUrl: '/images/widgets/invested-by-sector.png',
    });
  }

  try {
    // If no search term, return all widgets
    if (!searchTerm.trim()) {
      if (onSuccess) {
        onSuccess(widgets);
      }
      return;
    }

    const filteredWidgets = widgets.filter((widget) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        widget.name.toLowerCase().includes(searchLower) ||
        widget.description.toLowerCase().includes(searchLower)
      );
    });

    if (filteredWidgets.length === 0 && onEmpty) {
      onEmpty();
      return;
    }

    if (onSuccess) {
      onSuccess(filteredWidgets);
    }
  } catch (error) {
    if (onError) {
      onError(error as Error);
    }
  }
};
